// The MWIS area forecast: which area (suggested from the points, overridable),
// the parsed text for the day(s) in the window, and the stored PDF. `forecasts`
// is the fetch being shown, as for the point forecasts.
import { useState } from "react";
import axios from "axios";
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, CircularProgress,
  MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { openInNew } from "../geo";
import { mwisDaysInWindow } from "./forecast";
import SafetySection from "./SafetySection";
import { SAFETY_API, fmtDateTime, openForecastPdf } from "./format";

const AUTO = "";

export default function MwisPanel({ assessment, forecasts, config, onChange, onFetch, fetching }) {
  const [pdfBusy, setPdfBusy] = useState(false);
  const decided = assessment.status === "Decided";
  const forecast = forecasts.find(f => f.provider === "mwis");
  const mwis = forecast?.mwis;
  const suggestion = assessment.mwis_suggestion;
  const chosenKey = assessment.mwis_area || suggestion?.key || null;
  const chosenName = config.mwis_areas.find(a => a.key === chosenKey)?.name;
  // A forecast for a different area than the one now chosen is stale.
  const stale = forecast && chosenKey && forecast.meta?.area_key !== chosenKey;
  const inWindow = mwis ? mwisDaysInWindow(mwis, assessment.window_start, assessment.window_end) : [];
  const others = (mwis?.days || []).filter(d => !inWindow.includes(d));

  const setArea = (key) => {
    axios.put(`${SAFETY_API}/assessments/${assessment.id}`, { mwis_area: key || null })
      .then(res => onChange(res.data)).catch(() => {});
  };
  const pdf = () => {
    setPdfBusy(true);
    openForecastPdf(forecast.id).catch(() => {}).finally(() => setPdfBusy(false));
  };

  const summary = !chosenKey ? (
    <Typography variant="body2" color="text.secondary">No MWIS area chosen.</Typography>
  ) : !mwis ? (
    <Typography variant="body2" color="text.secondary">{chosenName} — {forecast?.error ? "the fetch failed." : "not fetched yet."}</Typography>
  ) : (
    <Stack spacing={0.5}>
      <Typography variant="body2" color="text.secondary">
        {forecast.meta?.area_name} · issued {mwis.updated || "—"}
      </Typography>
      {inWindow.length === 0 ? (
        <Typography variant="body2" color="text.secondary">Doesn't cover the race day yet.</Typography>
      ) : inWindow.map(d => (
        <Typography variant="body2" key={d.date || d.label}>
          <Box component="span" sx={{ fontWeight: 600 }}>{d.label || d.date}:</Box>{" "}
          {d.headline || "no headline"}
        </Typography>
      ))}
    </Stack>
  );

  return (
    <SafetySection id="mwis" title="Mountain Weather Information Service" summary={summary}
      subtitle={<>MWIS writes an area forecast for the mountains, three days ahead. It's fetched with the other
        forecasts, and its PDF is kept as the copy of record.</>}>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { sm: "center" }, mb: 2 }}>
        <TextField select size="small" label="MWIS area" sx={{ minWidth: 300 }} disabled={decided}
                   value={assessment.mwis_area || AUTO} onChange={e => setArea(e.target.value)}>
          <MenuItem value={AUTO}>
            {suggestion ? `Automatic — ${suggestion.name}` : "Automatic — none nearby"}
          </MenuItem>
          {config.mwis_areas.map(a => <MenuItem key={a.key} value={a.key}>{a.name}</MenuItem>)}
        </TextField>
        {!assessment.mwis_area && suggestion && (
          <Typography variant="body2" color="text.secondary">{suggestion.reason}</Typography>
        )}
      </Stack>

      {!chosenKey && (
        <Alert severity="info">
          The points aren't in or near an MWIS area. Choose one if the event is in the hills.
        </Alert>
      )}
      {chosenKey && !forecast && (
        <Alert severity="info" action={
          <Button color="inherit" size="small" onClick={onFetch} disabled={fetching || !assessment.points.length}>
            Fetch
          </Button>}>
          Not fetched yet — it comes with the forecasts.
        </Alert>
      )}
      {stale && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          The forecast below is for {forecast.meta?.area_name}, not {chosenName}. Fetch again to update it.
        </Alert>
      )}
      {forecast?.error && <Alert severity="error" sx={{ mb: 1 }}>{forecast.error}</Alert>}

      {mwis && (
        <>
          <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap", mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {forecast.meta?.area_name} · issued {mwis.updated || "—"} · fetched {fmtDateTime(forecast.fetched_at)}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            {forecast.has_pdf && (
              <Button size="small" onClick={pdf} disabled={pdfBusy}
                      startIcon={pdfBusy ? <CircularProgress size={14} /> : <PictureAsPdfIcon />}>
                Stored PDF
              </Button>
            )}
            {forecast.source_url && (
              <Button size="small" endIcon={<OpenInNewIcon />} onClick={() => openInNew(forecast.source_url)}>
                MWIS page
              </Button>
            )}
          </Stack>
          {forecast.meta?.pdf_error && (
            <Alert severity="warning" sx={{ mb: 1 }}>The PDF couldn't be stored: {forecast.meta.pdf_error}</Alert>
          )}

          {inWindow.length === 0 && (
            <Alert severity="info" sx={{ mb: 1 }}>
              MWIS doesn't cover the race day yet (it's published three days ahead). The planning
              outlook below is the longer view.
            </Alert>
          )}
          {inWindow.map(d => <MwisDay key={d.date || d.label} day={d} />)}

          {mwis.planning_outlook && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Planning outlook</Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>{mwis.planning_outlook}</Typography>
            </Box>
          )}

          {others.length > 0 && (
            <Box sx={{ mt: 2 }}>
              {others.map(d => (
                <Accordion key={d.date || d.label} disableGutters variant="outlined">
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body2">{d.label || d.date} (outside the window)</Typography>
                  </AccordionSummary>
                  <AccordionDetails><MwisDay day={d} bare /></AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </>
      )}
    </SafetySection>
  );
}

function MwisDay({ day, bare }) {
  return (
    <Box sx={bare ? undefined : { border: "1px solid", borderColor: "divider", borderRadius: 1, p: 2, mb: 1 }}>
      {!bare && <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{day.label || day.date}</Typography>}
      {day.headline && <Typography sx={{ fontStyle: "italic", mb: 1 }}>{day.headline}</Typography>}
      <Box component="dl" sx={{ m: 0, display: "grid", gridTemplateColumns: { xs: "1fr", md: "220px 1fr" }, columnGap: 2, rowGap: 1 }}>
        {day.sections.map(s => (
          <Box key={s.question} sx={{ display: "contents" }}>
            <Typography component="dt" variant="body2" sx={{ fontWeight: 600 }}>{s.question}</Typography>
            <Box component="dd" sx={{ m: 0 }}>
              {s.headline && <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.headline}</Typography>}
              <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>{s.text}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
      {day.summary && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          All areas: {day.summary}
        </Typography>
      )}
    </Box>
  );
}
